export type PixKeyType = 'cpf' | 'cnpj' | 'email' | 'phone' | 'evp'

function onlyDigits(value: string) {
  return (value || '').replace(/\D/g, '')
}

function crc16(payload: string) {
  let crc = 0xffff
  for (let i = 0; i < payload.length; i += 1) {
    crc ^= payload.charCodeAt(i) << 8
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1
      crc &= 0xffff
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0')
}

function emv(id: string, value: string) {
  const content = value || ''
  return `${id}${String(content.length).padStart(2, '0')}${content}`
}

function sanitizeName(value: string) {
  return (value || 'BONITA FM')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 25)
    .toUpperCase() || 'BONITA FM'
}

function sanitizeCity(value: string) {
  return (value || 'SAO JOSE DO SERIDO')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 15)
    .toUpperCase() || 'SAO JOSE'
}

function sanitizeMessage(value: string) {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 25)
}

export function detectPixKeyType(rawKey: string): PixKeyType {
  const key = (rawKey || '').trim()
  if (!key) return 'evp'
  if (key.includes('@')) return 'email'
  const digits = onlyDigits(key)
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key)) return 'evp'
  if (digits.length === 11 && !key.startsWith('+') && !key.includes('(')) return 'cpf'
  if (digits.length === 14) return 'cnpj'
  if (digits.length >= 10 && digits.length <= 13) return 'phone'
  return 'evp'
}

export function normalizePixKey(rawKey: string, type?: PixKeyType) {
  const key = (rawKey || '').trim()
  const resolved = type || detectPixKeyType(key)
  if (resolved === 'cpf' || resolved === 'cnpj') return onlyDigits(key)
  if (resolved === 'phone') {
    const digits = onlyDigits(key)
    if (digits.startsWith('55')) return `+${digits}`
    return `+55${digits}`
  }
  if (resolved === 'email') return key.toLowerCase()
  return key
}

export function formatPixKeyLabel(type: PixKeyType) {
  if (type === 'cpf') return 'CPF'
  if (type === 'cnpj') return 'CNPJ'
  if (type === 'email') return 'E-mail'
  if (type === 'phone') return 'Telefone'
  return 'Chave aleatória'
}

export function buildPixPayload(options: {
  pixKey: string
  pixKeyType?: PixKeyType | string
  beneficiaryName?: string
  beneficiaryCity?: string
  message?: string
}) {
  const key = normalizePixKey(options.pixKey, options.pixKeyType as PixKeyType | undefined)
  if (!key) return ''

  const description = sanitizeMessage(options.message || '')
  const merchantAccount =
    emv('00', 'br.gov.bcb.pix') +
    emv('01', key) +
    (description ? emv('02', description) : '')

  const base =
    emv('00', '01') +
    emv('26', merchantAccount) +
    emv('52', '0000') +
    emv('53', '986') +
    emv('58', 'BR') +
    emv('59', sanitizeName(options.beneficiaryName || '')) +
    emv('60', sanitizeCity(options.beneficiaryCity || '')) +
    emv('62', emv('05', '***')) +
    '6304'

  return `${base}${crc16(base)}`
}
